import React, { useState } from 'react'

import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked'
import { Button, IconButton } from '@mui/material'
import { useWatch } from 'react-hook-form'
import { UrlProvider } from 'urls'

import { Flag } from 'components/_base'
import { CompaniesFilters } from 'components/companies'
import RequestChange from 'components/company/RequestChange'
import FormToggle from 'components/input/FormToggle'
import { Table } from 'components/table'
import { INITIAL_COMPANIES_FILTERS } from 'constants/companies-filters'
import { withForm } from 'decorators/withForm'
import { useCompanies } from 'hooks/useCompanies'
import { usePaginatedTableMeta } from 'hooks/usePaginatedTableMeta'
import { emptyCompanyData } from 'models/company-model'
import { getChangeRequestInitialData } from 'utils/data-change-requests'
import { exportCompanies } from 'utils/exportCompanies'
import { findLinkedInIndustryByCode } from 'utils/industries'
import { findNaceCode } from 'utils/nace'

type CompaniesProps = {
  change: (...args: any[]) => any
}

const Companies = ({ change }: CompaniesProps) => {
  const { meta, setPage, setRowsPerPage, setFilters } = usePaginatedTableMeta(
    {
      filters: INITIAL_COMPANIES_FILTERS,
    },
    'companies',
  )
  const { data, isLoading, error } = useCompanies(meta)
  const selectedCompanies = useWatch() as Record<string, any>

  const [modalOpen, setModalOpen] = useState(false)
  const [selectAll, setSelectAll] = useState(false)

  const companies = data?.items ?? []

  const handleSelectAll = () => {
    companies.forEach(({ uuid }: any) => change(uuid, !selectAll))
    setSelectAll(!selectAll)
  }

  const getConfig = () => [
    {
      title: (
        <IconButton aria-label="Select all" onClick={handleSelectAll} size="large">
          <RadioButtonCheckedIcon />
        </IconButton>
      ),
      headerStyle: { width: 80 },
      noRowClick: true,
      render: ({ uuid }: any) => <FormToggle name={uuid} />,
    },
    {
      title: 'Name',
      field: 'name',
      maxWidth: 200,
    },
    {
      title: 'Website',
      field: 'url',
      maxWidth: 200,
    },
    {
      title: 'Country',
      render: ({ country }: any) => <Flag country={country} />,
    },
    {
      title: 'City',
      field: 'address.city',
    },
    {
      title: 'Nace code (Industry)',
      render: ({ nace_code }: any) => findNaceCode(nace_code?.code)?.name || '',
    },
    {
      title: 'Linkedin Industry',
      render: ({ linkedin_industry_code }: any) =>
        findLinkedInIndustryByCode(linkedin_industry_code?.code)?.category || '',
    },
  ]

  const selected = selectedCompanies || {}
  const noneSelected = !Object.values(selected).some(Boolean)
  const selectedCount = Object.values(selected).filter(Boolean).length
  const selectedUuids = Object.keys(selected).filter((key: any) => selected[key])

  return (
    <>
      <Table
        config={getConfig()}
        count={data?.count}
        data={companies}
        error={error ? 'Error occurred when loading companies data' : undefined}
        getRowLink={(company: any) => UrlProvider.getUrl('company', { uuid: company.uuid })}
        hover
        loading={isLoading}
        onChangePage={setPage}
        onChangeRowsPerPage={setRowsPerPage}
        page={meta.page}
        rowsPerPage={meta.rowsPerPage}
        title="Companies"
        toolbar={
          <>
            <CompaniesFilters
              count={data?.count}
              initialValues={meta.filters}
              onExport={(format: any) => exportCompanies(meta, format)}
              setFilters={setFilters}
            />
            <Button color="primary" disabled={noneSelected} onClick={() => setModalOpen(true)} variant="contained">
              Request change ({selectedCount})
            </Button>
          </>
        }
      />
      {modalOpen && (
        <RequestChange
          initialValues={getChangeRequestInitialData({ company: emptyCompanyData() })}
          onClose={() => setModalOpen(false)}
          onSubmit={() => setModalOpen(false)}
          open={modalOpen}
          uuids={selectedUuids}
        />
      )}
    </>
  )
}

export default withForm({
  form: 'companiesSelectionForm',
})(Companies)
